import {
  MAX_DISPLAY_CONTENTS_MAP,
  MaxDisplayContents,
  useContentFeedQuery,
} from './use-content-feed-query';
import { useEffect, useMemo, useRef } from 'react';
import { ContentHomepageModule } from '../../models/home-page-custom/types';
import { Content } from '../../models/content/types';
import useFeatureFlag from '../use-feature-flag';
import { Feature } from '../../models/features/features';
import { useDispatch } from 'react-redux';
import FeedService from '../../services/feed';
import { useQueries } from '@tanstack/react-query';
import { contentOperations } from '../../models/content';

/**
 * Configuration for a single homepage feed query
 */
export type HomepageFeedQueryConfig = {
  contentModule: ContentHomepageModule;
  order: number;
  enabled?: boolean;
};

export const getPerPage = (
  contentModule: ContentHomepageModule,
  order: number
) => {
  const { display_type: displayType } = contentModule.attributes.settings ?? {};
  if (!displayType || !(displayType in MAX_DISPLAY_CONTENTS_MAP)) {
    return 0;
  }
  const maxDisplayContents =
    MAX_DISPLAY_CONTENTS_MAP[displayType as keyof MaxDisplayContents];

  // Preserve existing behavior when no meaningful order is provided.
  if (order <= 0) {
    return maxDisplayContents;
  }

  // For later modules, progressively over-fetch so that deduplication
  // still leaves enough items to fully populate each module.
  return maxDisplayContents * order + maxDisplayContents;
};

/**
 * Helper function to create query options for a single module
 */
const createModuleQueryOptions = ({
  contentModule,
  perPage,
  customHomepageEnabled,
}: {
  contentModule: ContentHomepageModule;
  perPage: number;
  customHomepageEnabled: boolean;
}) => {
  const {
    source,
    reference_ids,
    display_type: displayType,
    only_include_followed_topics,
    content_inclusion,
  } = contentModule.attributes.settings ?? {};

  const filter = (): string | undefined => {
    if (source === 'latest') return 'newest';
    if (source === 'topic') return undefined;
    return source;
  };

  return {
    filter: filter(),
    perPage: perPage,
    includeTypes:
      displayType === 'gallery' && !customHomepageEnabled
        ? ['image']
        : undefined,
    homepageModuleParams: {
      homepage_content_channel_ids:
        source === 'topic' ? reference_ids : undefined,
      homepage_initiative_ids:
        source === 'initiative' ? reference_ids : undefined,
      only_followed_channels:
        only_include_followed_topics === true ? true : undefined,
      content_type:
        content_inclusion && content_inclusion !== 'all'
          ? content_inclusion
          : undefined,
    },
  };
};

/**
 * Hook that creates content feed queries for multiple homepage modules
 *
 * @param contentModules - Array of homepage module configurations with their order and perPage
 * @returns Array of query results, one for each module
 */
export const useHomepageFeedByModulesQuery = (
  contentModules: HomepageFeedQueryConfig[]
): Array<{
  data: Content[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}> => {
  const dispatch = useDispatch();
  const tagUserEnabled = useFeatureFlag(Feature.TAG_USER_ENABLED);
  const advancedSearchEnabled = useFeatureFlag(Feature.IMPROVED_SEARCH);
  const customHomepageEnabled = useFeatureFlag(Feature.CUSTOM_HOMEPAGE_ENABLED);

  // Create queries array with proper dependencies
  const queriesConfig = useMemo(
    () =>
      contentModules.map(({ contentModule, order, enabled }) => {
        const perPage = getPerPage(contentModule, order);
        const queryOptions = createModuleQueryOptions({
          contentModule,
          perPage,
          customHomepageEnabled: !!customHomepageEnabled,
        });

        return {
          queryKey: [
            'homepageFeed',
            contentModule.id,
            order,
            perPage,
            queryOptions.filter,
            JSON.stringify(queryOptions.homepageModuleParams),
            tagUserEnabled,
            advancedSearchEnabled,
          ] as const,
          queryFn: async () => {
            const feedService = new FeedService({
              filter: queryOptions.filter,
              includeTypes: queryOptions.includeTypes,
              homepageModuleParams: queryOptions.homepageModuleParams,
              mention_format: tagUserEnabled ? 'with_metadata' : undefined,
              useLegacyMetadata: !advancedSearchEnabled,
            });

            const response = await feedService.fetch({
              page: 1,
              perPage: queryOptions.perPage,
            });

            const items = (response.feed || response.contents)
              .map((fi) => fi.attributes)
              .filter((fi) =>
                Object.prototype.hasOwnProperty.call(fi, 'content_type')
              );

            return items.slice(0, queryOptions.perPage);
          },
          enabled: enabled !== undefined ? enabled : !!contentModule,
        };
      }),
    [contentModules, tagUserEnabled, advancedSearchEnabled]
  );

  // Use useQueries to run all queries in parallel
  const queries = useQueries({
    queries: queriesConfig,
  });

  // Dispatch contents to Redux store (matching useContentFeedQuery behavior)
  // Use a ref to track previous data to avoid unnecessary dispatches
  const previousDataRef = useRef<string>('');
  useEffect(() => {
    const allContents = queries
      .map((query) => query.data as Content[] | undefined)
      .filter((data): data is Content[] => Array.isArray(data))
      .flat();

    // Create a stable key from the data to avoid unnecessary dispatches
    const dataKey = JSON.stringify(allContents.map((c) => c.id));
    if (allContents.length > 0 && dataKey !== previousDataRef.current) {
      previousDataRef.current = dataKey;
      dispatch(contentOperations.addContents(allContents));
    }
  }, [queries, dispatch]);

  // Transform the results to match the expected return type
  // Memoize based on actual data values to prevent unnecessary re-renders
  const queriesIdentifier = useMemo(
    () =>
      queries
        .map((q) => {
          const dataIds =
            (q.data as Content[] | undefined)?.map((c) => c.id).join(',') || '';
          return `${dataIds}|${q.isLoading}|${q.isSuccess}|${q.isError}`;
        })
        .join('||'),
    [queries]
  );

  return useMemo(
    () =>
      queries.map((query) => ({
        data: (query.data as Content[]) || [],
        isLoading: query.isLoading,
        isSuccess: query.isSuccess,
        isError: query.isError,
        error: query.error as Error | null,
        refetch: query.refetch,
      })),
    // Only depend on queriesKey - the actual queries array reference changes but content doesn't
    // queriesKey is derived from queries, so this is safe
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queriesIdentifier]
  );
};

/**
 * Hook that creates a content feed query based on homepage module data
 *
/**
 * Hook that creates a content feed query based on homepage module data
 * 
 * @param contentModule - The homepage module configuration
 * @returns Query response object from useContentFeedQuery
 */
export const useHomepageFeedQuery = ({
  contentModule,
  perPage = 20,
}: {
  contentModule?: ContentHomepageModule;
  perPage?: number;
}) => {
  const {
    source,
    reference_ids,
    display_type: displayType,
    only_include_followed_topics,
    content_inclusion,
  } = contentModule?.attributes.settings || {};

  const customHomepageEnabled = useFeatureFlag(Feature.CUSTOM_HOMEPAGE_ENABLED);

  const filter = (): string | undefined => {
    if (source === 'latest') return 'newest';
    if (source === 'topic') return undefined;
    return source;
  };

  return useContentFeedQuery({
    queryEnabled: !!contentModule,
    filter: filter(),
    perPage: perPage,
    includeTypes:
      displayType === 'gallery' && !customHomepageEnabled
        ? ['image']
        : undefined,
    homepageModuleParams: {
      homepage_content_channel_ids:
        source === 'topic' ? reference_ids : undefined,
      homepage_initiative_ids:
        source === 'initiative' ? reference_ids : undefined,
      only_followed_channels:
        only_include_followed_topics === true ? true : undefined,
      content_type:
        content_inclusion && content_inclusion !== 'all'
          ? content_inclusion
          : undefined,
    },
  });
};
